import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../styles/pages/_subscription-plans.scss';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { API_CONFIG } from '../config/api';

interface SubscriptionPlan {
  _id: string;
  name: string;
  description: string;
  unit_name: string;
  token_rate: number;
  message_rate: number;
  currency: string;
  billing_interval: string;
  billing_interval_count: number;
  free_tokens_limit: number;
  free_messages_limit: number;
  price: number;
  is_active: boolean;
  sort_order: number;
  sampleCosts: {
    lowUsage: number;
    mediumUsage: number;
    highUsage: number;
  };
  metadata?: {
    features?: string[];
  };
  createdAt: string;
  updatedAt: string;
}

const SubscriptionPlansPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const isComponentMountedRef = useRef(true);
  const [isRestoringPlans, setIsRestoringPlans] = useState(false);

  // Modal states
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ isOpen: boolean; planId: string | null; planName: string | null }>({
    isOpen: false,
    planId: null,
    planName: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch plans from API
  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_PLANS_ADMIN}?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription plans');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch subscription plans');
      }

      setSubscriptionPlans(result.data || []);
    } catch (err) {

      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchPlans();
    return () => {
      isComponentMountedRef.current = false;
    };
  }, []);

  // Refetch when page gains focus (user returns from create/edit page)
  useEffect(() => {
    const handleFocus = () => {
      if (isComponentMountedRef.current) {
        fetchPlans();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const handleCreateNewPlan = () => {
    navigate('/panel/admin/subscription-plans/edit');
  };

  const handleRestoreDefaultPlans = async () => {
    if (!window.confirm('This will restore the default subscription plans (Free, Basic, Premium, Enterprise). Continue?')) {
      return;
    }

    try {
      setIsRestoringPlans(true);
      setError(null);
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/billing/plans/restore/defaults`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to restore default plans');
      }

      // Success - refresh plans
      toast.success(`✓ Default plans restored! ${data.restoredCount} new plans created.`);
      
      // Refresh the plans list
      setTimeout(() => {
        fetchPlans();
      }, 500);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore default plans';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsRestoringPlans(false);
    }
  };

  const handleArchivePlan = async (planId: string) => {
    if (!window.confirm('Are you sure you want to archive this subscription plan?')) {
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_PLANS}/${planId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ is_active: false })
      });

      if (!response.ok) {
        throw new Error('Failed to archive plan');
      }

      // Refresh the plans list
      setSubscriptionPlans(prev => prev.map(plan =>
        plan._id === planId ? { ...plan, is_active: false } : plan
      ));
    } catch (err) {

      setError(err instanceof Error ? err.message : 'Failed to archive plan');
    }
  };

  const handleEditPlan = (planId: string) => {
    navigate(`/panel/admin/subscription-plans/edit/${planId}`);
  };

  const handleRestorePlan = async (planId: string) => {
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_PLANS}/${planId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ is_active: true })
      });

      if (!response.ok) {
        throw new Error('Failed to restore plan');
      }

      // Refresh the plans list
      setSubscriptionPlans(prev => prev.map(plan =>
        plan._id === planId ? { ...plan, is_active: true } : plan
      ));
    } catch (err) {

      setError(err instanceof Error ? err.message : 'Failed to restore plan');
    }
  };

  const handleDeletePlan = async (planId: string) => {
    try {
      setIsDeleting(true);
      const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_PLANS}/${planId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to permanently delete plan');
      }

      // Remove plan from the list only after successful deletion
      const deletedPlan = subscriptionPlans.find(p => p._id === planId);
      setSubscriptionPlans(prev => prev.filter(plan => plan._id !== planId));
      
      // Show success toast
      if (deletedPlan) {
        toast.success(`Plan "${deletedPlan.name}" has been permanently deleted.`);
      }

      // Close delete confirmation modal
      setDeleteConfirmModal({ isOpen: false, planId: null, planName: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to permanently delete plan';
      setError(errorMessage);
      setDeleteConfirmModal({ isOpen: false, planId: null, planName: null });
    } finally {
      setIsDeleting(false);
    }
  };

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as Element).closest('.subscription-plans__filter-dropdown')) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredPlans = subscriptionPlans.filter(plan => {
    const matchesSearch = plan.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All Status' ||
      (statusFilter === 'Active' && plan.is_active) ||
      (statusFilter === 'Inactive' && !plan.is_active);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="subscription-plans">
        <div className="subscription-plans__container">
          <div className="subscription-plans__loading">Loading subscription plans...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-plans">
        <div className="subscription-plans__container">
          <div className="subscription-plans__error">
            <p>Error loading subscription plans: {error}</p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-plans">
      <div className="subscription-plans__container">
        <div className="subscription-plans__header">
          <div className="subscription-plans__header-content">
            <div className="subscription-plans__title-section">
              <h1 className="subscription-plans__title">Subscription Plans</h1>
              <p className="subscription-plans__subtitle">Manage, create, and edit subscription plans for your users.</p>
            </div>
          </div>
        </div>

        <div className="subscription-plans__filters">
          <div className="subscription-plans__search">
            <div className="subscription-plans__search-icon">
              <span className="material-symbols-outlined">search</span>
            </div>
            <input
              type="text"
              placeholder="Search by plan name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="subscription-plans__search-input"
            />
          </div>
          <div className="subscription-plans__actions">
            {subscriptionPlans.length > 0 && !subscriptionPlans.some(p => p.name === 'Enterprise') && (
              <Button 
                onClick={handleRestoreDefaultPlans}
                disabled={isRestoringPlans}
                className="subscription-plans__create-btn subscription-plans__create-btn--warning"
                title="Restore missing default plans including Enterprise"
              >
                <span className="material-symbols-outlined">restart_alt</span>
                <span>{isRestoringPlans ? 'Restoring...' : 'Restore Default Plans'}</span>
              </Button>
            )}
            <Button onClick={handleCreateNewPlan} className="subscription-plans__create-btn">
              <span className="material-symbols-outlined">add</span>
              <span>Create New Plan</span>
            </Button>
            <div className="subscription-plans__status-filter subscription-plans__filter-dropdown">
              <button
                className={`subscription-plans__filter-btn ${statusFilter !== 'All Status' ? 'subscription-plans__filter-btn--active' : ''}`}
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              >
                <div className="subscription-plans__filter-content">
                  <span className="material-symbols-outlined">verified</span>
                  <p>{statusFilter}</p>
                </div>
                <span className="material-symbols-outlined subscription-plans__arrow">expand_more</span>
              </button>
              {showStatusDropdown && (
                <div className="subscription-plans__dropdown-menu" onClick={() => setShowStatusDropdown(false)}>
                  <button
                    className="subscription-plans__dropdown-item"
                    onClick={() => { setStatusFilter('All Status'); setShowStatusDropdown(false); }}
                  >
                    All Status
                  </button>
                  <button
                    className="subscription-plans__dropdown-item"
                    onClick={() => { setStatusFilter('Active'); setShowStatusDropdown(false); }}
                  >
                    Active
                  </button>
                  <button
                    className="subscription-plans__dropdown-item"
                    onClick={() => { setStatusFilter('Inactive'); setShowStatusDropdown(false); }}
                  >
                    Inactive
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {filteredPlans.length === 0 ? (
          <div className="subscription-plans__empty">
            <p>No subscription plans found.</p>
            <Button onClick={handleCreateNewPlan} className="subscription-plans__create-btn">
              <span className="material-symbols-outlined">add</span>
              <span>Create Your First Plan</span>
            </Button>
          </div>
        ) : (
          <div className="subscription-plans__grid">
            {filteredPlans.map((plan) => (
              <div key={plan._id} className="subscription-plans__card">
                <div className="subscription-plans__card-content">
                  <h3 className="subscription-plans__plan-name">{plan.name}</h3>
                  <p className="subscription-plans__plan-description">{plan.description}</p>
                  <p className="subscription-plans__plan-price">
                    {plan.currency} {plan.price?.toFixed(2) || '0.00'}
                    <span className="subscription-plans__plan-period">
                      {' '}/ {plan.billing_interval_count > 1
                        ? `${plan.billing_interval_count} ${plan.billing_interval}s`
                        : plan.billing_interval}
                    </span>
                  </p>
                  <div className="subscription-plans__plan-status">
                    <span className={`subscription-plans__status-badge subscription-plans__status-badge--${plan.is_active ? 'active' : 'inactive'}`}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className={`subscription-plans__period-badge subscription-plans__period-badge--${plan.billing_interval}`}>
                      {plan.billing_interval === 'month' ? 'Monthly' : 'Yearly'}
                    </span>
                  </div>
                  {plan.free_messages_limit !== undefined && plan.free_messages_limit !== null && (
                    <p className="subscription-plans__plan-limits">
                      {plan.free_messages_limit} free messages per {plan.billing_interval}
                    </p>
                  )}
                  {plan.metadata?.features && plan.metadata.features.length > 0 ? (
                    <div className="subscription-plans__plan-features">
                      <h4 className="subscription-plans__features-title">Features:</h4>
                      <ul className="subscription-plans__features-list">
                        {plan.metadata.features.map((feature, index) => (
                          <li key={index} className="subscription-plans__feature-item">
                            <span className="material-symbols-outlined subscription-plans__feature-icon">check</span>
                            <span className="subscription-plans__feature-text">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="subscription-plans__plan-features">
                      <button
                        onClick={() => window.open('https://www.magiteque.life/contact-us//', '_blank', 'noopener,noreferrer')}
                        className="pricing-card__button pricing-card__button--enterprise"
                      >
                        Choose Enterprise
                      </button>
                    </div>
                  )}
                </div>
                <div className="subscription-plans__card-actions">
                  {plan.is_active ? (
                    <>
                      {['Free', 'Basic', 'Premium', 'Enterprise'].includes(plan.name) ? (
                        <div className="subscription-plans__action-btn subscription-plans__action-btn--secondary subscription-plans__action-btn--disabled">
                          <span className="material-symbols-outlined">lock</span>
                          Protected
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setDeleteConfirmModal({
                              isOpen: true,
                              planId: plan._id,
                              planName: plan.name
                            });
                          }}
                          className="subscription-plans__action-btn subscription-plans__action-btn--danger"
                        >
                          <span className="material-symbols-outlined">delete</span>
                          Delete
                        </button>
                      )}
                      <button
                        onClick={() => handleEditPlan(plan._id)}
                        className="subscription-plans__action-btn subscription-plans__action-btn--primary"
                      >
                        <span className="material-symbols-outlined">edit</span>
                        Edit
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setDeleteConfirmModal({
                            isOpen: true,
                            planId: plan._id,
                            planName: plan.name
                          });
                        }}
                        className="subscription-plans__action-btn subscription-plans__action-btn--danger"
                      >
                        <span className="material-symbols-outlined">delete</span>
                        Delete Permanently
                      </button>
                      <button
                        onClick={() => handleRestorePlan(plan._id)}
                        className="subscription-plans__action-btn subscription-plans__action-btn--secondary"
                      >
                        <span className="material-symbols-outlined">unarchive</span>
                        Restore
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          className="delete-confirmation-modal"
          isDanger={true}
          closeLabel="Cancel"
          isOpen={deleteConfirmModal.isOpen}
          title="Delete Plan?"
          onClose={() => setDeleteConfirmModal({ isOpen: false, planId: null, planName: null })}
          actionLabel={isDeleting ? "Deleting..." : "Delete Plan"}
          onAction={() => {
            if (deleteConfirmModal.planId) {
              handleDeletePlan(deleteConfirmModal.planId);
            }
          }}
          actionDisabled={isDeleting}
        >
          <div style={{ 
            padding: '0.75rem 0 0 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1.25rem',
            width: '100%',
          }}>
            {/* Warning Icon */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              flexShrink: 0,
            }}>
              <span style={{
                fontSize: '2rem',
              }}>⚠️</span>
            </div>

            {/* Confirmation Message - Single block */}
            <div style={{
              textAlign: 'center',
              width: '100%',
            }}>
              <p style={{
                fontSize: '0.875rem',
                marginBottom: '0.5rem',
                lineHeight: 1.4,
                color: '#888888',
                fontWeight: 400,
                margin: '0 0 0.5rem 0',
              }}>
                You're about to permanently delete
              </p>
              <p style={{
                fontSize: '1rem',
                fontWeight: 700,
                margin: '0',
                color: '#ffffff',
                wordBreak: 'break-word',
                letterSpacing: '-0.2px',
              }}>
                "{deleteConfirmModal.planName}"
              </p>
            </div>

            {/* Warning Box - Simplified */}
            <div style={{
              width: '100%',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              borderRadius: '8px',
              padding: '0.875rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start',
            }}>
              <span style={{
                fontSize: '0.95rem',
                flexShrink: 0,
                marginTop: '0.05rem',
              }}>
                ⚠️
              </span>
              <div>
                <p style={{
                  fontSize: '0.8rem',
                  margin: 0,
                  lineHeight: 1.5,
                  color: '#e0e0e0',
                  fontWeight: 500,
                }}>
                  <span style={{ color: '#ff6b6b', fontWeight: 700 }}>This action cannot be undone. </span>
                  All associated data will be permanently removed.
                </p>
              </div>
            </div>
          </div>
        </Modal>

      </div>
    </div>
  );
};

export default SubscriptionPlansPage;

